import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AppealType } from '~/shared/api/appealType'
import { getPhoneMaskByCountry } from '~/shared/config/countryConfig'
import { getObject } from '~/shared/lib/utils'
import { LinkDecorator } from '~/shared/ui/Buttons'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  MaskInput,
  NumericInput,
  TextareaInput,
} from '~/shared/ui/Form'
import { Container } from '~/shared/ui/Layout'
import {
  fetchSubdivisionByCarIdFx,
  fetchCarOptions,
  fetchDriverOptions,
  formSchema,
  FormValues,
} from './model'
import { useFormWatch } from './useFormWatch'

type MainFormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
  children?: ReactNode
  title: ReactNode
}

export function MainForm({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  onSuccess,
  defaultValues,
  children,
  title,
}: MainFormProps) {
  const { t } = useTranslation()

  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    defaultValues: { phone: '', ...defaultValues },
    resolver: zodResolver(formSchema),
  })

  const isSelectSubdivisionLoading = useStore(fetchSubdivisionByCarIdFx.pending)

  useFormWatch(form, isEditing || isCreating)

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        {title}
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <AsyncAutocompleteInput
              label='form.driver'
              placeholder='form.select'
              name='driverOption'
              fetchOptions={fetchDriverOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              disableClearable
              endDecorator={
                disabled && (
                  <LinkDecorator
                    to={`/drivers/${
                      getObject(defaultValues?.driverOption)?.id
                    }`}
                    tooltipText={t('button.go to driver card')}
                  />
                )
              }
            />
          </Grid>
          <Grid xs={6}>
            <MaskInput
              name='phone'
              label='form.phone'
              placeholder='form.phone'
              skeletonShow={isLoading}
              readOnly
              mask={getPhoneMaskByCountry()}
            />
          </Grid>
          <Grid xs={6}>
            <AsyncAutocompleteInput
              label='form.car'
              name='carOption'
              placeholder='form.car'
              fetchOptions={fetchCarOptions}
              loading={isSelectSubdivisionLoading}
              disableClearable
              endDecorator={
                disabled && (
                  <LinkDecorator
                    to={`/cars/${getObject(defaultValues?.carOption)?.id}`}
                    tooltipText={t('button.go to car card')}
                  />
                )
              }
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              label='form.subdivision'
              placeholder='form.subdivision'
              name='subdivisionOption'
              options={[]}
              loading={isSelectSubdivisionLoading}
              skeletonShow={isLoading}
              readOnly
            />
          </Grid>
          <Grid xs={6} container>
            <Grid xs={12}>
              <AsyncAutocompleteInput
                label='form.request type'
                disableClearable
                name='appealTypeOption'
                placeholder='form.select'
                fetchOptions={AppealType.fetchOptions}
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={12}>
              <NumericInput
                placeholder='form.enter amount'
                label='form.amount'
                name='amount'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
          <Grid xs={6}>
            <TextareaInput
              label='form.comment'
              name='creatorComment'
              placeholder='form.enter comment'
              sx={{
                height: '138px',
              }}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      {children}

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
